// eslint-disable-next-line no-restricted-imports
import styled from '@emotion/styled';
import { Flex, InfoIcon, Link, Typography } from '@iheartjane/reefer';
import type { ReactNode } from 'react';
import { useRef, useState } from 'react';

import { useIsOverflowing, useKioskSpacing } from '@jane/kiosk/hooks';

const LeftContent = styled(Flex)({
  position: 'absolute' as const,
  paddingTop: 4,
  top: 16,
  left: 16,
});

const RightContent = styled(Flex)({
  position: 'absolute' as const,
  right: 16,
  bottom: 16,
});

const StaticInfoIcon = styled(InfoIcon)({
  minWidth: 16,
  minHeight: 16,
  alignItems: 'center',
});

const StyledLink = styled(Link)(({ theme }) => ({
  backgroundColor: theme.colors.grays.ultralight,
}));

const BannerContent = styled.div(({ $wrap }: { $wrap: boolean }) => ({
  flexDirection: 'row' as const,
  alignContent: 'flex-start',
  display: 'flex',
  overflow: 'hidden',
  maxWidth: '85%',
  marginLeft: 24,
  flexWrap: $wrap ? ('wrap' as const) : ('nowrap' as const),
  whiteSpace: 'nowrap',
}));

const BannerContainer = styled(Flex)(({ theme }) => ({
  backgroundColor: theme.colors.grays.ultralight,
  borderRadius: theme.borderRadius.sm,
}));

export const Banner = ({
  children,
  expandText,
  onExpand,
}: {
  children: ReactNode;
  expandText?: string;
  onExpand?: () => void;
}) => {
  const { getKioskSpacing, defaultSpacing } = useKioskSpacing();
  const [expanded, setExpanded] = useState(false);
  const overflowingText = useRef<HTMLDivElement | null>(null);
  const overflowActive = useIsOverflowing(overflowingText);
  const currentExpandText =
    expandText ?? (expanded ? 'Show less' : 'Show more');

  return (
    <Flex mt={defaultSpacing} px={getKioskSpacing(64, 48)} width="100%">
      <BannerContainer
        alignItems="center"
        flexDirection="row"
        position="relative"
        justifyContent="space-between"
        p={16}
        width="100%"
      >
        <LeftContent>
          <StaticInfoIcon size="sm" />
        </LeftContent>
        <BannerContent ref={overflowingText} $wrap={expanded}>
          {children}
        </BannerContent>
        {(overflowActive || expanded || !!expandText) && (
          <RightContent>
            <StyledLink
              ariaLabel={currentExpandText}
              onClick={onExpand ?? (() => setExpanded(!expanded))}
            >
              <Typography maxLines={1}>{currentExpandText}</Typography>
            </StyledLink>
          </RightContent>
        )}
      </BannerContainer>
    </Flex>
  );
};
